type PageResult<TNode> = {
  hasNextPage: boolean;
  afterCursor: string | undefined;
  nodes: TNode[];
};

/**
 * Drives cursor-based relay pagination to completion and returns the full
 * accumulated node list. Deduplicates by `id` across pages using a Set.
 *
 * @param initialFetch - Fetches the first page.
 * @param fetchMore    - Fetches subsequent pages given the previous endCursor.
 * @param extractPage  - Extracts `{ hasNextPage, afterCursor, nodes }` from a
 *                       raw query result.
 * @param isCancelled  - When this returns `true` the loop stops and returns
 *                       whatever has been accumulated so far. Useful for
 *                       aborting mid-flight when a search term changes.
 */
export async function fetchAllPages<
  TResult,
  TNode extends { id: string | null | undefined }
>(opts: {
  initialFetch: () => Promise<TResult>;
  fetchMore: (after: string | undefined) => Promise<TResult>;
  extractPage: (result: TResult) => PageResult<TNode>;
  isCancelled?: () => boolean;
}): Promise<TNode[]> {
  const {
    initialFetch,
    fetchMore,
    extractPage,
    isCancelled = () => false
  } = opts;

  const initial = extractPage(await initialFetch());
  let { hasNextPage, afterCursor } = initial;
  const seenIds = new Set(initial.nodes.map((n) => n.id));
  let nodes = initial.nodes;

  while (hasNextPage) {
    if (isCancelled()) {
      return nodes;
    }
    const page = extractPage(await fetchMore(afterCursor));
    hasNextPage = page.hasNextPage;
    afterCursor = page.afterCursor;
    const newNodes = page.nodes.filter((n) => !seenIds.has(n.id));
    newNodes.forEach((n) => seenIds.add(n.id));
    nodes = [...nodes, ...newNodes];
  }

  return nodes;
}
